import { TOTALS_CELL_BG, TOTALS_ROW_CLASS } from 'components/TableTotals/styles'
import { useEffect, useState } from 'react'

import { Pagination } from '../Pagination/Pagination'

interface PlainTableProps {
  /**
   * Header of Table
   */
  header: Array<string | JSX.Element>
  /**
   * Table data
   */
  data?: Array<Array<string | number | JSX.Element | undefined> | null>

  /**
   * Optional totals row rendered in a <tfoot>. Sits outside the body, so it is unaffected by
   * pagination and stays put when `stickyHeader` freezes the chrome.
   */
  footer?: Array<string | number | JSX.Element | undefined>

  classNames?: string[]

  widthClass?: string

  thClass?: string
  tdClass?: string

  trClasses?: Record<number, string>
  isLastColumnStatic?: boolean
  hasPagination?: boolean
  itemCountPerPage?: number
  /** Freeze the header row while the body scrolls. Needs `maxHeightClass` to have any effect. */
  stickyHeader?: boolean
  /** Caps the scroll container, e.g. "max-h-[70vh]", so the body scrolls inside it. */
  maxHeightClass?: string
}

export const PlainTable = ({
  header = [],
  data = [],
  footer,
  classNames = [],
  widthClass = 'w-full',
  thClass = '',
  tdClass = 'px-6 py-4 whitespace-nowrap',
  trClasses = {},
  isLastColumnStatic = false,
  hasPagination = false,
  itemCountPerPage = 10,
  stickyHeader = false,
  maxHeightClass = '',
}: PlainTableProps) => {
  const [pageNum, setPageNum] = useState(0)

  // Filtering down while on a later page would otherwise leave the user on a page that no
  // longer exists, looking at an empty table.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(data.length / itemCountPerPage) - 1)
    if (pageNum > lastPage) setPageNum(lastPage)
  }, [data.length, itemCountPerPage, pageNum])

  const onPageNavigate = (num: number) => {
    setPageNum(num)
  }

  // The frozen header and the static last column can both apply to one cell, and that corner
  // has to sit above both of them.
  const headerCellClass = (index: number) => {
    const isCorner = isLastColumnStatic && header.length - 1 === index
    const parts = ['px-6', 'py-3']
    if (stickyHeader) parts.push('sticky', 'top-0', 'bg-slate-50', 'dark:bg-gray-700')
    if (isCorner) parts.push('sticky', 'right-0', 'bg-gray-200', 'dark:bg-gray-800')
    parts.push(isCorner ? (stickyHeader ? 'z-30' : 'z-10') : stickyHeader ? 'z-20' : '')
    return parts.filter(Boolean).join(' ')
  }

  const footerCellClass = (index: number) => {
    const isCorner = isLastColumnStatic && footer !== undefined && footer.length - 1 === index
    const parts = [TOTALS_CELL_BG]
    if (stickyHeader) parts.push('sticky', 'bottom-0')
    if (isCorner) parts.push('sticky', 'right-0')
    parts.push(isCorner ? (stickyHeader ? 'z-30' : 'z-10') : stickyHeader ? 'z-20' : '')
    return parts.filter(Boolean).join(' ')
  }

  return (
    <>
      <div className={`relative overflow-auto mb-6 rounded-lg ${maxHeightClass}`}>
        <table className={`${widthClass} text-sm text-left text-gray-900 dark:text-gray-400 pl-6`}>
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              {header.map((item, index) => (
                <th
                  scope="col"
                  className={`${headerCellClass(index)} ${thClass} ${classNames[index] || ''}`}
                  key={index}
                >
                  {typeof item === 'string' ? <span dangerouslySetInnerHTML={{ __html: item }} /> : item}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-[14.5px] text-gray-900">
            {data.map((row, index) => {
              if (hasPagination && (index < pageNum * itemCountPerPage || index >= (pageNum + 1) * itemCountPerPage))
                return
              return (
                <tr
                  key={index}
                  className={`border-b border-slate-100 transition-colors hover:bg-sky-50 ${trClasses[index] || ''}`}
                >
                  {row &&
                    row.map((item, rIndex) => {
                      if ((item as any) === true) item = 'Y'
                      if ((item as any) === false) item = 'N'

                      return (
                        <td
                          className={`${tdClass} ${rIndex == 0 && 'font-medium text-gray-900 dark:text-white'} ${
                            isLastColumnStatic && row.length - 1 == rIndex
                              ? `sticky right-0 z-10 ${index % 2 ? 'bg-gray-200' : 'bg-white'}`
                              : ''
                          }`}
                          key={rIndex}
                        >
                          {item}
                        </td>
                      )
                    })}
                </tr>
              )
            })}
          </tbody>
          {footer && (
            <tfoot className={TOTALS_ROW_CLASS}>
              <tr>
                {footer.map((item, index) => (
                  <td className={`${tdClass} ${footerCellClass(index)} ${classNames[index] || ''}`} key={index}>
                    {item}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {hasPagination && (
        <Pagination
          totalCount={data.length}
          itemCountPerPage={itemCountPerPage}
          onNavigate={onPageNavigate}
          pageNum={pageNum}
        />
      )}
    </>
  )
}
